import React, { useEffect, useMemo, useState } from 'react';
import { MapContainer, TileLayer, Marker, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix default marker icons (Leaflet + bundler issue)
const departIcon = new L.DivIcon({
  className: '',
  html: '<div style="background:#000;color:#fff;width:28px;height:28px;border-radius:9999px;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:600;box-shadow:0 2px 8px rgba(0,0,0,.35);border:2px solid #fff;">A</div>',
  iconSize: [28, 28],
  iconAnchor: [14, 14]
});
const arriveeIcon = new L.DivIcon({
  className: '',
  html: '<div style="background:#b8895a;color:#fff;width:28px;height:28px;border-radius:9999px;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:600;box-shadow:0 2px 8px rgba(0,0,0,.35);border:2px solid #fff;">B</div>',
  iconSize: [28, 28],
  iconAnchor: [14, 14]
});

const FitBounds = ({ points }) => {
  const map = useMap();
  useEffect(() => {
    if (!points || points.length < 2) return;
    const bounds = L.latLngBounds(points);
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 13 });
  }, [points, map]);
  return null;
};

const RouteMap = ({ fromAddr, toAddr, lang = 'fr' }) => {
  const [route, setRoute] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  // Coords stored as [lon, lat] in BAN. Leaflet wants [lat, lon].
  const fromLL = useMemo(
    () => fromAddr?.coordinates ? [fromAddr.coordinates[1], fromAddr.coordinates[0]] : null,
    [fromAddr]
  );
  const toLL = useMemo(
    () => toAddr?.coordinates ? [toAddr.coordinates[1], toAddr.coordinates[0]] : null,
    [toAddr]
  );

  useEffect(() => {
    setRoute(null);
    setErr(null);
    if (!fromLL || !toLL) return;
    const ctrl = new AbortController();
    setLoading(true);
    const url = `https://router.project-osrm.org/route/v1/driving/${fromAddr.coordinates[0]},${fromAddr.coordinates[1]};${toAddr.coordinates[0]},${toAddr.coordinates[1]}?overview=full&geometries=geojson`;
    fetch(url, { signal: ctrl.signal })
      .then(r => r.json())
      .then(data => {
        if (data?.routes?.[0]?.geometry?.coordinates) {
          const coords = data.routes[0].geometry.coordinates.map(c => [c[1], c[0]]);
          setRoute({
            coords,
            distanceKm: Math.round(data.routes[0].distance / 1000),
            durationMin: Math.round(data.routes[0].duration / 60)
          });
        } else {
          setErr('no-route');
        }
      })
      .catch((e) => { if (e.name !== 'AbortError') setErr('fetch'); })
      .finally(() => setLoading(false));
    return () => ctrl.abort();
  }, [fromLL, toLL, fromAddr, toAddr]);

  if (!fromLL || !toLL) {
    return (
      <div className="bg-[#f6f4f1] border border-black/10 p-6 text-center text-xs tracking-[0.2em] uppercase text-black/50">
        {lang === 'fr' ? 'Saisissez départ et arrivée pour voir l\u2019itinéraire' : 'Enter pickup and drop-off to see the route'}
      </div>
    );
  }

  const center = [(fromLL[0] + toLL[0]) / 2, (fromLL[1] + toLL[1]) / 2];
  const fallbackLine = [fromLL, toLL];
  const line = route?.coords || fallbackLine;

  return (
    <div className="border border-black/10 bg-white" data-testid="route-map-wrapper">
      <div className="relative">
        <MapContainer
          center={center}
          zoom={10}
          scrollWheelZoom={false}
          style={{ height: 280, width: '100%' }}
          attributionControl={false}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; OpenStreetMap'
          />
          <Marker position={fromLL} icon={departIcon} />
          <Marker position={toLL} icon={arriveeIcon} />
          <Polyline positions={line} pathOptions={{ color: '#000', weight: 4, opacity: 0.9 }} />
          <FitBounds points={fallbackLine} />
        </MapContainer>
        {loading && (
          <div className="absolute top-2 right-2 bg-white/95 px-3 py-1 text-[10px] tracking-[0.2em] uppercase border border-black/10">
            {lang === 'fr' ? 'Calcul…' : 'Routing…'}
          </div>
        )}
      </div>
      <div className="flex items-center justify-between px-4 py-3 border-t border-black/10 text-xs">
        <div className="flex items-center gap-4 text-black/70">
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-2.5 h-2.5 rounded-full bg-black" /> {lang === 'fr' ? 'Départ' : 'Pickup'}
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: '#b8895a' }} /> {lang === 'fr' ? 'Arrivée' : 'Drop-off'}
          </span>
        </div>
        {route && (
          <div className="text-black/70 tracking-wide">
            <span className="font-medium text-black">{route.distanceKm} km</span> • {route.durationMin} min
          </div>
        )}
        {err && (
          <div className="text-black/50 italic">{lang === 'fr' ? 'Itinéraire approximatif' : 'Approximate route'}</div>
        )}
      </div>
    </div>
  );
};

export default RouteMap;
